/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Input } from 'antd'
import type { GetProps } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'

type SearchProps = GetProps<typeof Input.Search>

const { Search } = Input

type SearchInputProps = {
  onSearch: SearchProps['onSearch']
  placeholder?: string
  disabled?: boolean
}

const SearchInput = ({ onSearch, placeholder, disabled }: SearchInputProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      onSearch?.(value)
    }, 300),
    [onSearch]
  )

  useEffect(() => {
    debouncedSearch(searchTerm)
  }, [searchTerm, debouncedSearch])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  return (
    <Search
      placeholder={placeholder}
      onChange={handleChange}
      value={searchTerm}
      disabled={disabled}
      variant='borderless'
      css={css`
        width: 100%;
        border-radius: 4px;
        border: 1px solid ${colors.NEUTRAL_200};
        background-color: ${colors.SECONDARY_400};
        .ant-input-affix-wrapper {
          ${paragraphMedium}
          ${weightRegular}
          color: ${colors.NEUTRAL_100};
        }
        .ant-input-search-button {
          border: none;
          background-color: ${colors.SECONDARY_400};
        }
        .ant-input-outlined {
          background-color: ${colors.SECONDARY_400};
          border: none;
        }
        .ant-input-affix-wrapper:hover {
          border: none;
        }
      `}
    />
  )
}

const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), waitFor)
  }
}

export default SearchInput
